import { Button, Tag, message, Tooltip } from 'antd';
import { COLUMN_EXTRA_LARGE, COLUMN_MEDIUM } from 'constants/table';
import { checkValue, formatDate, truncateString } from 'utils';
import { thousandFormatter } from 'utils/forms/formatter';
import {
  clientPaymentStatuses,
  paymentStatusToText,
  paymentTypes,
} from 'utils/handbooks/paymentsHandbook';
import { priceRender, renderWithTooltip } from 'utils/table';

const cardPaymentColumns = [
  {
    title: '№',
    dataIndex: 'order',
    key: 'order',
  },
  {
    width: COLUMN_EXTRA_LARGE,
    title: 'Компания',
    dataIndex: 'company_name',
    key: 'company_name',
    render: (name) => renderWithTooltip(name, 60),
  },
  {
    title: 'Сумма',
    dataIndex: 'amount',
    key: 'amount',
    render: priceRender,
  },
  {
    title: 'Дата',
    dataIndex: 'update_date',
    key: 'update_date',
    render: (date) => {
      if (date === checkValue(date)) {
        return formatDate(date, 'DD.MM.YYYY, HH.mm');
      }

      return checkValue(date);
    },
  },
  {
    title: 'Статус',
    dataIndex: 'status',
    key: 'status',
    render: (status) => <Tag>{paymentStatusToText[status]}</Tag>,
  },
  {
    title: 'Ссылка на оплату',
    dataIndex: 'redirect_url',
    key: 'redirect_url',
    render: (url) => {
      if (!url) {
        return '---';
      }

      return (
        <Button
          type="link"
          size="small"
          onClick={() => {
            navigator.clipboard.writeText(url);
            message.success('Успешно скопировано');
          }}
        >
          Скопировать
        </Button>
      );
    },
  },
  {
    title: 'Терминал',
    dataIndex: 'terminal',
    key: 'terminal',
    render: checkValue,
  },
  {
    title: 'РРН',
    dataIndex: 'rrn',
    key: 'rrn',
    render: checkValue,
  },
];

const clientsPaymentColumns = [
  {
    title: 'ID',
    dataIndex: 'id',
    key: 'id',
  },
  {
    title: 'Тип',
    dataIndex: 'type',
    key: 'type',
    render: (type) => paymentTypes[type],
  },
  {
    title: 'Сумма',
    dataIndex: 'amount',
    key: 'amount',
    render: priceRender,
  },
  {
    title: 'Дата',
    dataIndex: 'create_date',
    key: 'create_date',
    render: (date) => {
      if (date === checkValue(date)) {
        return formatDate(date, 'DD.MM.YYYY, HH.mm');
      }

      return checkValue(date);
    },
  },
  {
    width: COLUMN_MEDIUM,
    title: 'Статус',
    dataIndex: 'status',
    key: 'status',
    render: (status) => {
      if (!status || clientPaymentStatuses[status] === undefined) {
        return '--';
      }

      return (
        <Tooltip placement="topLeft" title={clientPaymentStatuses[status]}>
          <Tag>{truncateString(clientPaymentStatuses[status], 20)}</Tag>
        </Tooltip>
      );
    },
  },
  {
    title: 'Период',
    dataIndex: 'period',
    key: 'period',
  },
  {
    title: 'Дата выставления',
    dataIndex: 'confirmation_date',
    key: 'confirmation_date',
    render: (date) => {
      if (date === checkValue(date)) {
        return formatDate(date, 'DD.MM.YYYY, HH.mm');
      }

      return checkValue(date);
    },
  },
];

const cardReversalPaymentColumns = [
  {
    title: 'ID',
    dataIndex: 'id',
    key: 'id',
  },
  {
    title: 'Компания',
    dataIndex: 'company_name',
    key: 'company_name',
    render: (name) => renderWithTooltip(name),
  },
  {
    title: 'Сумма возврата',
    dataIndex: 'amount',
    key: 'amount',
    render: priceRender,
  },
  {
    title: 'Сумма',
    dataIndex: 'org_amount',
    key: 'org_amount',
    render: priceRender,
  },
  {
    title: 'Статус',
    dataIndex: 'status',
    key: 'status',
    render: (status) => <Tag>{paymentStatusToText[status]}</Tag>,
  },
  {
    title: '№ транзакции',
    dataIndex: 'rrn',
    key: 'rrn',
  },
  {
    title: 'Дата',
    dataIndex: 'timestamp',
    key: 'timestamp',
    render: (date) => {
      if (date === checkValue(date)) {
        return formatDate(date, 'DD.MM.YYYY, HH.mm');
      }

      return checkValue(date);
    },
  },
  {
    title: '№ платежа банка',
    dataIndex: 'int_ref',
    key: 'int_ref',
  },
];

const bccColumns = [
  {
    title: 'ID',
    dataIndex: 'id',
    key: 'id',
  },
  {
    title: 'Тип',
    dataIndex: 'type',
    key: 'type',
    render: (type) => paymentTypes[type],
  },
  {
    title: 'Сумма',
    dataIndex: 'amount',
    key: 'amount',
    render: priceRender,
  },

  {
    title: 'Статус',
    dataIndex: 'status_name',
    key: 'status_name',
    render: (status) => <Tag>{status}</Tag>,
  },
  {
    title: 'ИИН',
    dataIndex: 'iin',
    key: 'iin',
  },
  {
    title: 'ФИО',
    dataIndex: 'fio',
    key: 'fio',
    render: (name) => renderWithTooltip(name, 60),
  },
  {
    title: 'УГД',
    dataIndex: 'tax',
    key: 'tax',
  },
  {
    title: 'Создан',
    dataIndex: 'create_date',
    key: 'create_date',
    render: (date) => {
      if (date === checkValue(date)) {
        return formatDate(date, 'DD.MM.YYYY, HH.mm');
      }

      return checkValue(date);
    },
  },
  {
    title: 'Проведен',
    dataIndex: 'send_date',
    key: 'send_date',
    render: (date) => {
      if (date === checkValue(date)) {
        return formatDate(date, 'DD.MM.YYYY, HH.mm');
      }

      return checkValue(date);
    },
  },
];

const bccPartnerColumns = [
  {
    title: 'ID',
    dataIndex: 'id',
    key: 'id',
  },
  {
    title: 'Тип',
    dataIndex: 'type',
    key: 'type',
    render: (type) => paymentTypes[type],
  },
  {
    title: 'Сумма',
    dataIndex: 'amount',
    key: 'amount',
    render: priceRender,
  },

  {
    title: 'Статус',
    dataIndex: 'status_name',
    key: 'status_name',
    render: (status) => <Tag>{status}</Tag>,
  },
  {
    title: 'ИИН',
    dataIndex: 'iin',
    key: 'iin',
  },
  {
    title: 'ФИО',
    dataIndex: 'fio',
    key: 'fio',
    render: (name) => renderWithTooltip(name),
  },
  {
    title: 'Создан',
    dataIndex: 'create_date',
    key: 'create_date',
    render: (date) => {
      if (date === checkValue(date)) {
        return formatDate(date, 'DD.MM.YYYY, HH.mm');
      }

      return checkValue(date);
    },
  },
  {
    title: 'Проведен',
    dataIndex: 'send_date',
    key: 'send_date',
    render: (date) => {
      if (date === checkValue(date)) {
        return formatDate(date, 'DD.MM.YYYY, HH.mm');
      }

      return checkValue(date);
    },
  },
];

const bccNestedColumns = [
  {
    title: 'Фамилия',
    dataIndex: 'fm',
    key: 'fm',
  },
  {
    title: 'Имя',
    dataIndex: 'nm',
    key: 'nm',
  },
  {
    title: 'Отчество',
    dataIndex: 'ft',
    key: 'ft',
  },
  {
    title: 'ИИН',
    dataIndex: 'iin',
    key: 'iin',
  },
  {
    title: 'Дата рождения',
    dataIndex: 'dt',
    key: 'dt',
  },
  {
    title: 'Период',
    dataIndex: 'period',
    key: 'period',
  },
  {
    title: 'Сумма (тг)',
    dataIndex: 'amount',
    key: 'amount',
    render: (amount) => (amount ? thousandFormatter(amount) : '---'),
  },
];

export {
  clientsPaymentColumns,
  cardPaymentColumns,
  cardReversalPaymentColumns,
  bccColumns,
  bccPartnerColumns,
  bccNestedColumns,
};
